import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Clock, Users, Brain, ChevronRight } from 'lucide-react';
import { haptic } from '../../utils/haptics';
import Button from '../ui/Button';
import StatTile from '../ui/StatTile';
import AuraIcon from '../ui/AuraIcon.jsx';
import { useAuraConfig } from '../../hooks/useAuraConfig';
import {
  AURA_PENDING_TEXT,
  AURA_REASON_FALLBACK_TEXT,
  getAuraReasonText,
  inferAuraReason,
} from '../../utils/auraReason';

const SessionCompletedAnimation = ({ session, onContinue }) => {
  const [currentAnimation, setCurrentAnimation] = useState(0);
  const [showStats, setShowStats] = useState(false);
  const [animationComplete, setAnimationComplete] = useState(false);
  const [motivationalMessage, setMotivationalMessage] = useState('');
  const { config: auraConfig } = useAuraConfig();

  // Motivierende Nachrichten basierend auf der Dauer und Anzahl der Teilnehmer.
  // Seit Okt 2026 ruhige Aussagen ohne Ausrufezeichen (Kommunikation.md).
  const getMotivationalMessage = () => {
    const durationMs = session.duration || 0;
    const durationMinutes = Math.floor(durationMs / 60000);
    const participantCount = session.participants?.length || 0;

    if (durationMinutes > 60) {
      return "Über eine Stunde drangeblieben. Deine Ausdauer zahlt sich aus.";
    } else if (participantCount >= 3) {
      return "Zu dritt oder mehr gelernt. Gemeinsam geht es leichter.";
    } else if (!session.isFocusMode && session.auraEarned > 100) {
      return "Deine Aura wächst mit jeder Session.";
    }

    const messages = [
      "Jede Lernsession bringt dich deinen Zielen näher.",
      "Jede Minute Lernen zählt.",
      "Du investierst in das, was du kannst.",
      "Dranbleiben ist der Schlüssel.",
      "Dein Einsatz lohnt sich."
    ];

    return messages[Math.floor(Math.random() * messages.length)];
  };

  // Animation-Dateien
  const animationFiles = [
    '/assets/session/success1.webp',
    '/assets/session/success2.gif',
    '/assets/session/success3.webp'
  ];

  useEffect(() => {
    // Haptisches Feedback bei Session-Abschluss (aufsteigendes Level-Up-Crescendo)
    haptic.levelUp();

    // Zufällige Animation auswählen
    const randomIndex = Math.floor(Math.random() * animationFiles.length);
    setCurrentAnimation(randomIndex);

    // Spruch einmalig beim Mount setzen
    setMotivationalMessage(getMotivationalMessage());

    // Nach kurzer Verzögerung Statistiken einblenden
    const statsTimer = setTimeout(() => {
      setShowStats(true);
    }, 500);

    // Nach 2 Sekunden Button freigeben
    const completeTimer = setTimeout(() => {
      setAnimationComplete(true);
    }, 2000);

    return () => {
      clearTimeout(statsTimer);
      clearTimeout(completeTimer);
    };
  }, []);

  // Berechne Statistiken
  const durationMs = session.duration || 0;
  const durationMinutes = Math.floor(durationMs / 60000);
  const participantCount = session.participants?.length || 1;

  // Der Fokus-Wert ist die Vordergrundzeit des Browser-Tabs geteilt durch die
  // Session-Dauer. Fuer eine Solo-Fokus-Session ist das die Kennzahl des Features:
  // Wie lange bin ich drangeblieben?
  //
  // Fuer eine Lern-Session zu zweit misst dieselbe Zahl etwas ganz anderes - naemlich,
  // wie lange jemand auf den Bildschirm gestarrt hat, statt mit dem Lernpartner zu
  // reden. Sie stand hier bis August 2026 trotzdem, weil beim Entfernen des
  // Gruppen-Fokus (Juli 2026) nur der Tracker abgeschaltet wurde, nicht die Anzeige.
  // Ergebnis waren Werte wie "Fokus 3%" nach einer gelungenen Partnersession.
  // Ohne Messung (z. B. auto-beendete Fokus-Session) bleibt der Wert leer und die
  // Kachel weg - vorher stand dort "0 %" oder ein geschaetztes "85 %".
  const focusRatio = session.focusStats?.focusRatio;
  const focusTimeMs = session.focusStats?.totalFocusTime;
  let focusPercentage = null;
  if (focusRatio != null) {
    focusPercentage = Math.round(focusRatio * 100);
  } else if (Number.isFinite(focusTimeMs) && durationMs > 0) {
    focusPercentage = Math.round((focusTimeMs / durationMs) * 100);
  }

  // Nur zeigen, was fuer diese Session auch gilt: Bei einer Solo-Session ist
  // "Teilnehmer: 1" keine Information, bei einer Partnersession ist es der Fokus-Wert.
  // Fokus-Sessions geben grundsaetzlich keine Aura - eine Kachel "+0 Aura" las sich
  // dort wie eine verweigerte Gutschrift (Okt 2026).
  // auraEarned null/undefined = noch offen (manuelles Beenden: der Server rechnet
  // erst beim Feedback-Abschluss). Dann keine Kachel statt einer geschaetzten Zahl.
  const auraKnown = Number.isFinite(session.auraEarned);
  const stats = [
    { icon: Clock, label: 'Dauer', value: `${durationMinutes} Min`, accent: 'primary' },
    session.isFocusMode || !auraKnown
      ? null
      : { icon: AuraIcon, label: 'Aura erhalten', value: `+${session.auraEarned}`, accent: 'aura' },
    participantCount > 1
      ? { icon: Users, label: 'Teilnehmende', value: participantCount, accent: 'primary' }
      : null,
    session.isFocusMode && focusPercentage != null
      ? { icon: Brain, label: 'Fokus', value: `${focusPercentage}%`, accent: 'primary' }
      : null,
  ].filter(Boolean);

  // Warum 0 Aura: Grund vom Server (auraReason), sonst nur, was sicher ableitbar
  // ist (zu wenige Validierte, zu kurz). Fokus-Sessions bekommen keinen Satz.
  const minSessionDuration = auraConfig?.limits?.minSessionDuration;
  const auraReason = session.auraReason !== undefined
    ? session.auraReason
    : inferAuraReason({
      validatedCount: session.validatedCount,
      durationMinutes,
      minSessionDuration,
    });
  let auraNote = null;
  if (!session.isFocusMode && !(auraKnown && session.auraEarned > 0)) {
    const reasonText = auraReason ? getAuraReasonText(auraReason, { minSessionDuration }) : null;
    if (reasonText) {
      auraNote = reasonText;
    } else {
      auraNote = auraKnown ? AURA_REASON_FALLBACK_TEXT : AURA_PENDING_TEXT;
    }
  }

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto bg-surface">
      {/* Header - clean & minimal.
          Das Overlay liegt mit fixed inset-0 ausserhalb der Safe-Area-Regeln aus
          index.css. Die globale Regel `.min-h-screen .sticky.top-0 { top: env() }`
          traf den Kopf trotzdem: Er klebte bei top=Statusleiste, stand im Fluss aber
          bei 0 - und rutschte darum um eine Statusleistenhoehe nach unten ueber das
          GIF (iPhone, Sep 2026). Darum: top:0 inline erzwingen und die Safe-Area als
          eigenes Padding im Kopf mitfuehren (Formel wie WelcomeTour). */}
      <div
        className="sticky z-10 bg-surface border-b border-border px-4 pb-3"
        style={{
          top: 0,
          paddingTop: 'calc(0.75rem + max(env(safe-area-inset-top, 0px), var(--native-safe-area-top, 0px)))',
        }}
      >
        {/* Gleiche Breite wie der Inhalt darunter: Kopf und Koerper liefen vorher
            mit max-w-2xl bzw. max-w-md auseinander. */}
        <div className="max-w-md mx-auto">
          <h1 className="text-center text-foreground font-semibold text-base">
            Session abgeschlossen
          </h1>
        </div>
      </div>

      {/* Main Content */}
      <div
        className="max-w-md mx-auto px-4 py-4 space-y-4"
        style={{ paddingBottom: 'calc(1rem + env(safe-area-inset-bottom, 0px))' }}
      >

        {/* Erfolgs-Animation + Nachricht */}
        <div className="text-center">
          {/* Dezentere, kleinere Animation */}
          <div className="w-48 h-48 mx-auto mb-3 rounded-xl overflow-hidden border border-border shadow-token-sm bg-surface">
            <img
              src={animationFiles[currentAnimation]}
              alt="Erfolgsanimation"
              className="w-full h-full object-cover"
            />
          </div>

          <h2 className="text-2xl font-bold text-foreground mb-1">
            Gut gemacht
          </h2>

          <p className="text-faint text-sm">
            {motivationalMessage}
          </p>
        </div>

        {/* Statistiken */}
        {showStats && (
          <div className="bg-surface-raised rounded-xl p-4 border border-border space-y-3 animate-fade-in">
            <h3 className="text-xs font-semibold text-faint uppercase tracking-wider">
              Session-Zusammenfassung
            </h3>

            {/* ui/StatTile statt vier handgebauter Kacheln. Nebeneffekt: Die
                Zusammenfassung sieht jetzt genauso aus wie die in
                SessionCompletedSummary - vorher waren es zwei verschiedene
                Bauweisen fuer dieselben Zahlen. */}
            <div className="grid grid-cols-2 gap-2">
              {stats.map((stat) => (
                <StatTile
                  key={stat.label}
                  icon={stat.icon}
                  label={stat.label}
                  value={stat.value}
                  accent={stat.accent}
                />
              ))}
            </div>

            {/* "+0 Aura" ohne Erklaerung liest sich wie ein Fehler - der Grund
                gehoert daneben, und zwar der echte (utils/auraReason.js). */}
            {auraNote && (
              <p className="text-xs text-muted-foreground" data-testid="aura-note">
                {auraNote}
              </p>
            )}
          </div>
        )}

        {/* Weiter-Button */}
        <div className={`transition-opacity duration-300 ${animationComplete ? 'opacity-100' : 'opacity-0'}`}>
          <Button
            variant="secondary"
            size="lg"
            onClick={onContinue}
            disabled={!animationComplete}
            fullWidth
          >
            Weiter zum Feedback
            <ChevronRight className="h-4 w-4" aria-hidden="true" />
          </Button>
        </div>
      </div>
    </div>
  );
};

SessionCompletedAnimation.propTypes = {
  session: PropTypes.shape({
    id: PropTypes.string.isRequired,
    topic: PropTypes.string.isRequired,
    duration: PropTypes.number.isRequired,
    auraEarned: PropTypes.number, // null = noch offen
    // Grund fuer 0 Aura vom Server (UNDER_MIN_DURATION, DAILY_BUDGET, AURA_DISABLED,
    // SOLO; null = vergeben). Fehlt das Feld, wird nur Sicheres abgeleitet.
    auraReason: PropTypes.string,
    validatedCount: PropTypes.number,
    isFocusMode: PropTypes.bool,
    participants: PropTypes.arrayOf(PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      name: PropTypes.string.isRequired,
      image: PropTypes.string
    })).isRequired,
    focusStats: PropTypes.shape({
      focusRatio: PropTypes.number,
      totalFocusTime: PropTypes.number
    })
  }).isRequired,
  onContinue: PropTypes.func.isRequired
};

export default SessionCompletedAnimation;
